import React, { useCallback, useEffect, createContext, useState } from 'react';
import * as http from 'utils/http';
import config from 'config';

const CarriersContext = createContext();
const CARRIER_PAGE_SIZE = 25;

const getCarriers = async (query = '') =>
  await http.get(`${config.API_URL}carriers/?page_size=${CARRIER_PAGE_SIZE}&${query}`);

function CarriersContextProvider({ children }) {
  const [mounted, setMounted] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [carrierData, setCarrierData] = useState({
    count: 0,
    next: '',
    previous: '',
    results: [],
  });
  const [order, setOrder] = useState({
    field: 'name',
    direction: '+',
  });
  const [routesFilter, setRoutesFilter] = useState('');
  const [carrierTypeFilter, setCarrierTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const clearFilters = useCallback(() => {
    setRoutesFilter('');
    setCarrierTypeFilter('');
    setCategoryFilter('');
    setPeriodFilter('');
    setStatusFilter('');
  }, [setRoutesFilter, setCarrierTypeFilter, setCategoryFilter, setPeriodFilter, setStatusFilter]);

  const flatMultiValues = useCallback((values) => {
    if (!values) return '';
    return values.map((item) => item.value).join();
  }, []);

  const fetchCarriers = useCallback(
    async function () {
      if (!mounted) {
        return;
      }

      try {
        setLoading(true);
        const query = `ordering=${order.direction}${
          order.field
        }&last_job=${periodFilter}&category=${flatMultiValues(
          categoryFilter
        )}&routes=${flatMultiValues(routesFilter)}&carrier_type=${flatMultiValues(
          carrierTypeFilter
        )}&status=${statusFilter}`;
        const data = await getCarriers(query);
        mounted && setCarriers(data.results);
        mounted && setCarrierData(data);
      } catch (err) {
        console.error(err);
      } finally {
        mounted && setLoading(false);
        mounted && setLoadingMore(false);
      }
    },
    [
      mounted,
      order,
      periodFilter,
      routesFilter,
      carrierTypeFilter,
      categoryFilter,
      statusFilter,
      flatMultiValues,
    ]
  );

  const fetchNextCarriers = useCallback(
    async function () {
      if (!mounted) {
        return;
      }

      try {
        setLoadingMore(true);
        const data = await http.get(carrierData.next);
        mounted && setCarriers((prevCarriers) => [...prevCarriers, ...data.results]);
        mounted && setCarrierData(data);
      } catch (err) {
        console.error(err);
      } finally {
        mounted && setLoading(false);
        mounted && setLoadingMore(false);
      }
    },
    [mounted, carrierData.next]
  );

  useEffect(() => {
    fetchCarriers();
  }, [fetchCarriers]);

  useEffect(() => {
    return () => {
      setMounted(false);
    };
  }, []);

  return (
    <CarriersContext.Provider
      value={{
        fetchCarriers,
        fetchNextCarriers,
        carrierData,
        carriers,
        loading,
        setOrder,
        order,
        loadingMore,
        periodFilter,
        setPeriodFilter,
        routesFilter,
        setRoutesFilter,
        categoryFilter,
        setCategoryFilter,
        carrierTypeFilter,
        setCarrierTypeFilter,
        statusFilter,
        setStatusFilter,
        clearFilters,
      }}
    >
      {children}
    </CarriersContext.Provider>
  );
}

export { CarriersContextProvider };
export default CarriersContext;
